import { useState, useEffect } from 'react';
import { Icons } from '../components/Icons';
import { StorageService } from '../services/storage';
import { LogService } from '../services/logService';
import { exportToJSON } from '../services/export';
import { useToast } from '../components/Toast';
import type { Log, LogLevel } from '../types';

interface LogLevelConfig {
  label: string;
  emoji: string;
  color: string;
}

interface LogFilter {
  level: string;
  category: string;
  search: string;
}

// Log levels configuration
const LOG_LEVELS: Record<LogLevel, LogLevelConfig> = {
  DEBUG: { label: 'Debug', emoji: '🔍', color: '#6c757d' },
  INFO: { label: 'Info', emoji: 'ℹ️', color: '#0dcaf0' },
  SUCCESS: { label: 'Success', emoji: '✅', color: '#198754' },
  WARNING: { label: 'Warning', emoji: '⚠️', color: '#ffc107' },
  ERROR: { label: 'Error', emoji: '❌', color: '#dc3545' },
  CRITICAL: { label: 'Critical', emoji: '🚨', color: '#6f42c1' }
};

/**
 * Logs Page - System logs viewer and management
 *
 * Features:
 * - Display all system logs with filtering
 * - Filter by level, category, and search
 * - Export logs to JSON/TXT
 * - Clear logs
 * - Auto-refresh
 */

export function Logs() {
  const [logs, setLogs] = useState<Log[]>([]);
  const [filter, setFilter] = useState<LogFilter>({ level: '', category: '', search: '' });
  const [filteredLogs, setFilteredLogs] = useState<Log[]>([]);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const toast = useToast();

  // Load logs from storage
  useEffect(() => {
    const loadLogs = () => {
      const storedLogs = StorageService.get<Log[]>('system_logs', []);

      // Add some demo logs if empty
      if (storedLogs.length === 0) {
        const demoLogs: Log[] = [
          { id: '1', level: 'INFO', category: 'System', message: 'Application démarrée', timestamp: Date.now() - 3600000 },
          { id: '2', level: 'SUCCESS', category: 'Portfolio', message: 'Portfolio chargé avec succès', timestamp: Date.now() - 3000000 },
          { id: '3', level: 'INFO', category: 'API', message: 'Récupération des prix...', timestamp: Date.now() - 2400000 },
          { id: '4', level: 'SUCCESS', category: 'API', message: 'Prix mis à jour pour 10 tokens', timestamp: Date.now() - 2340000 },
          { id: '5', level: 'WARNING', category: 'API', message: 'Limite de taux approchée (45/50 calls)', timestamp: Date.now() - 1800000 },
          { id: '6', level: 'INFO', category: 'Storage', message: 'Données sauvegardées dans localStorage', timestamp: Date.now() - 1200000 },
          { id: '7', level: 'DEBUG', category: 'Calculator', message: 'Calcul FIFO pour Bitcoin', data: { positions: 5, result: { realizedPL: 1250 } }, timestamp: Date.now() - 600000 },
          { id: '8', level: 'SUCCESS', category: 'Export', message: 'Export CSV généré avec succès', timestamp: Date.now() - 300000 },
          { id: '9', level: 'ERROR', category: 'API', message: 'Échec de récupération pour token xyz', data: { tokenId: 'xyz', error: 'Not found' }, timestamp: Date.now() - 120000 },
          { id: '10', level: 'INFO', category: 'UI', message: 'Mode sombre activé', timestamp: Date.now() - 60000 },
        ];
        setLogs(demoLogs);
        StorageService.set('system_logs', demoLogs);
      } else {
        setLogs(storedLogs);
      }
    };

    loadLogs();
  }, []);

  // Auto-refresh every 5 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      const storedLogs = StorageService.get<Log[]>('system_logs', []);
      setLogs(storedLogs);
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  // Filter logs
  useEffect(() => {
    let result = [...logs];

    // Filter by level
    if (filter.level) {
      result = result.filter((log) => log.level === filter.level);
    }

    // Filter by category
    if (filter.category) {
      const query = filter.category.toLowerCase();
      result = result.filter((log) => log.category?.toLowerCase().includes(query));
    }

    // Filter by search
    if (filter.search) {
      const query = filter.search.toLowerCase();
      result = result.filter((log) =>
        log.message?.toLowerCase().includes(query) ||
        log.category?.toLowerCase().includes(query) ||
        JSON.stringify(log.data || '').toLowerCase().includes(query)
      );
    }

    // Sort by timestamp (newest first)
    result.sort((a, b) => b.timestamp - a.timestamp);

    setFilteredLogs(result);
  }, [logs, filter]);

  const handleExport = (format: 'json' | 'txt') => {
    if (format === 'json') {
      exportToJSON(filteredLogs, 'system-logs.json');
      toast('Logs exportés en JSON', 'success');
    } else {
      const text = filteredLogs
        .map((log) => `[${new Date(log.timestamp).toLocaleString()}] [${log.level}] [${log.category}] ${log.message}`)
        .join('\n');

      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'system-logs.txt';
      a.click();
      URL.revokeObjectURL(url);

      toast('Logs exportés en TXT', 'success');
    }
  };

  const handleCopy = async () => {
    const text = filteredLogs
      .map((log) => `[${new Date(log.timestamp).toLocaleString()}] [${log.level}] [${log.category}] ${log.message}`)
      .join('\n');

    try {
      await navigator.clipboard.writeText(text);
      toast('Logs copiés dans le presse-papiers', 'success');
    } catch (e) {
      toast('Erreur lors de la copie', 'error');
    }
  };

  const handleClear = () => {
    if (confirm('Effacer tous les logs ?')) {
      setLogs([]);
      StorageService.set('system_logs', []);
      toast('Logs effacés', 'success');
    }
  };

  const handleGenerateReport = async () => {
    const success = await LogService.copyErrorReport();
    if (success) {
      toast('Rapport d\'erreur copié dans le presse-papiers', 'success');
    } else {
      toast('Erreur lors de la copie du rapport', 'error');
    }
  };

  const handleDownloadReport = () => {
    LogService.downloadErrorReport();
    toast('Rapport d\'erreur téléchargé', 'success');
  };

  // Stats
  const stats = {
    total: logs.length,
    debug: logs.filter(l => l.level === 'DEBUG').length,
    info: logs.filter(l => l.level === 'INFO').length,
    success: logs.filter(l => l.level === 'SUCCESS').length,
    warning: logs.filter(l => l.level === 'WARNING').length,
    error: logs.filter(l => l.level === 'ERROR').length,
    critical: logs.filter(l => l.level === 'CRITICAL').length,
  };

  return (
    <>
      {/* Header Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        <div className="stat-card">
          <div className="stat-label">Total Logs</div>
          <div className="stat-value">{stats.total}</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: LOG_LEVELS.SUCCESS.color }}>
          <div className="stat-label">{LOG_LEVELS.SUCCESS.emoji} Success</div>
          <div className="stat-value">{stats.success}</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: LOG_LEVELS.ERROR.color }}>
          <div className="stat-label">{LOG_LEVELS.ERROR.emoji} Errors</div>
          <div className="stat-value">{stats.error}</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: LOG_LEVELS.WARNING.color }}>
          <div className="stat-label">{LOG_LEVELS.WARNING.emoji} Warnings</div>
          <div className="stat-value">{stats.warning}</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: LOG_LEVELS.INFO.color }}>
          <div className="stat-label">{LOG_LEVELS.INFO.emoji} Info</div>
          <div className="stat-value">{stats.info}</div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="card">
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
          {/* Level Filter */}
          <select
            aria-label="Filter logs by level"
            value={filter.level}
            onChange={(e) => setFilter({ ...filter, level: e.target.value })}
            style={{ padding: '8px 12px', fontSize: '13px', borderRadius: '6px', minWidth: '140px' }}
          >
            <option value="">Tous les niveaux</option>
            {(Object.keys(LOG_LEVELS) as LogLevel[]).map((level) => (
              <option key={level} value={level}>
                {LOG_LEVELS[level].emoji} {LOG_LEVELS[level].label}
              </option>
            ))}
          </select>

          {/* Category Filter */}
          <input
            type="text"
            placeholder="Filtrer par catégorie..."
            value={filter.category}
            onChange={(e) => setFilter({ ...filter, category: e.target.value })}
            style={{ padding: '8px 12px', fontSize: '13px', borderRadius: '6px', minWidth: '150px' }}
          />

          {/* Search Filter */}
          <input
            type="text"
            placeholder="Rechercher dans les logs..."
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            style={{ padding: '8px 12px', fontSize: '13px', borderRadius: '6px', minWidth: '200px', flex: 1 }}
          />

          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {/* Auto-refresh */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', padding: '8px' }}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh
            </label>

            {/* Action Buttons */}
            <button onClick={handleGenerateReport} className="btn-primary" style={{ fontSize: '13px', padding: '8px 12px' }} title="Générer et copier un rapport d'erreur complet">
              <Icons.AlertCircle size={14} style={{ marginRight: '4px' }} />
              Rapport
            </button>
            <button onClick={handleDownloadReport} className="btn-ghost" style={{ fontSize: '13px', padding: '8px 12px' }} title="Télécharger le rapport d'erreur">
              <Icons.Download size={14} style={{ marginRight: '4px' }} />
              Rapport
            </button>
            <button onClick={() => handleExport('json')} className="btn-ghost" style={{ fontSize: '13px', padding: '8px 12px' }}>
              <Icons.Download size={14} style={{ marginRight: '4px' }} />
              JSON
            </button>
            <button onClick={() => handleExport('txt')} className="btn-ghost" style={{ fontSize: '13px', padding: '8px 12px' }}>
              <Icons.FileText size={14} style={{ marginRight: '4px' }} />
              TXT
            </button>
            <button onClick={handleCopy} className="btn-ghost" style={{ fontSize: '13px', padding: '8px 12px' }}>
              <Icons.Copy size={14} style={{ marginRight: '4px' }} />
              Copier
            </button>
            <button onClick={handleClear} className="btn-ghost" style={{ fontSize: '13px', padding: '8px 12px', color: '#dc3545' }}>
              <Icons.Trash size={14} style={{ marginRight: '4px' }} />
              Effacer
            </button>
          </div>
        </div>

        {/* Logs List */}
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {filteredLogs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--muted)' }}>
              <div style={{
                display: 'inline-flex',
                padding: '20px',
                borderRadius: '50%',
                background: 'rgba(99, 102, 241, 0.1)',
                marginBottom: '20px'
              }}>
                <Icons.FileText size={48} style={{ opacity: 0.5, color: 'var(--accent)' }} />
              </div>
              <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: 'var(--text)' }}>
                Aucun log à afficher
              </div>
              <div style={{ fontSize: '14px', color: 'var(--muted)', lineHeight: '1.6', maxWidth: '400px', margin: '0 auto' }}>
                {filter.level || filter.category || filter.search ? (
                  <>
                    Aucun log ne correspond à vos filtres.
                    <br />
                    Essayez de modifier ou réinitialiser les filtres.
                  </>
                ) : (
                  <>
                    Les logs système apparaîtront ici au fur et à mesure
                    <br />
                    des actions effectuées dans l'application.
                  </>
                )}
              </div>
              {(filter.level || filter.category || filter.search) && (
                <button
                  onClick={() => setFilter({ level: '', category: '', search: '' })}
                  style={{
                    marginTop: '20px',
                    padding: '10px 20px',
                    background: 'var(--accent)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          ) : (
            filteredLogs.map((log) => {
              const levelInfo = LOG_LEVELS[log.level] || LOG_LEVELS.INFO;
              const logKey = typeof log.id === 'string' ? log.id : String(log.id);

              return (
                <div
                  key={logKey}
                  style={{
                    background: 'var(--card-bg)',
                    border: `1px solid ${levelInfo.color}20`,
                    borderLeft: `4px solid ${levelInfo.color}`,
                    borderRadius: '6px',
                    padding: '12px',
                    marginBottom: '8px',
                    fontSize: '13px',
                    fontFamily: 'monospace'
                  }}
                >
                  {/* Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontWeight: 'bold', color: levelInfo.color }}>
                      {levelInfo.emoji} {levelInfo.label}
                    </span>
                    <span style={{ color: 'var(--muted)', fontSize: '11px' }}>
                      {new Date(log.timestamp).toLocaleString('fr-FR')}
                    </span>
                  </div>

                  {/* Message */}
                  <div style={{ fontWeight: '600', marginBottom: '4px', color: 'var(--text)' }}>
                    <span style={{ color: 'var(--muted)', fontSize: '12px' }}>[{log.category}]</span> {log.message}
                  </div>

                  {/* Data */}
                  {log.data && typeof log.data !== 'undefined' && (
                    <details style={{ marginTop: '8px' }}>
                      <summary style={{ cursor: 'pointer', color: 'var(--muted)', fontSize: '12px' }}>
                        📦 Voir les données
                      </summary>
                      <pre style={{
                        background: 'var(--bg)',
                        padding: '8px',
                        borderRadius: '4px',
                        marginTop: '6px',
                        fontSize: '11px',
                        overflow: 'auto',
                        maxHeight: '200px'
                      }}>
                        {String(JSON.stringify(log.data, null, 2))}
                      </pre>
                    </details>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}
